import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../../catalyst/button';
import { Input } from '../../catalyst/input';
import { ErrorMessage, FieldGroup, Field, Fieldset, Label } from '../../catalyst/fieldset';
import { type UseFormReturn, type UseFieldArrayReturn } from 'react-hook-form';
import { useToast } from '../../components/Toast';
import { type SupertypeFormData } from './types';

interface CategoriesSectionProps {
  form: UseFormReturn<SupertypeFormData>;
  categories: UseFieldArrayReturn<SupertypeFormData, 'categories'>;
  availableCategories: string[];
}

export function CategoriesSection({ form, categories, availableCategories }: CategoriesSectionProps) {
  const errors = form.formState.errors;
  const toast = useToast();

  return (
    <div className="p-4 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 flex flex-col gap-4">
      <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Categories</h2>

      {errors.categories && <p className="text-red-500 dark:text-red-400 text-sm">{errors.categories.message}</p>}

      <Fieldset>
        <FieldGroup>
          <Field>
            <Label>Category Score Multiplier</Label>
            <Input
              {...form.register('categoryScoreMultiplier', {
                valueAsNumber: true,
              })}
              {...{ min: 0, max: 1, step: 0.05 }}
              type="number"
            />
            {errors.categoryScoreMultiplier && (
              <ErrorMessage>{errors.categoryScoreMultiplier.message}</ErrorMessage>
            )}
          </Field>
        </FieldGroup>
      </Fieldset>

      {categories.fields.length > 0 && (
        <ul className="space-y-2 mb-2">
          {categories.fields.map((field, index) => (
            <li key={field.id} className="flex items-center gap-2">
              <Input
                {...form.register(`categories.${index}.0`)}
                list="available-categories"
                className="flex-2"
              />
              <Input
                className="flex-1"
                {...form.register(`categories.${index}.1`, {
                  valueAsNumber: true,
                })}
                defaultValue={1}
                type="number"
              />
              <Button onClick={() => categories.remove(index)} outline>
                <MinusIcon className="size-4" />
              </Button>
              <Button
                onClick={() => {
                  categories.insert(index + 1, [['', 1]]);
                  toast.success('Category added successfully');
                }}
                outline
              >
                <PlusIcon className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {categories.fields.length === 0 && (
        <Button onClick={() => categories.append([['', 1]])} outline>
          <PlusIcon className="size-4" /> Add Category
        </Button>
      )}

      <datalist id="available-categories">
        {availableCategories.map((item) => (
          <option key={item} value={item} />
        ))}
      </datalist>
    </div>
  );
}
